/**
 * Sizing for the new-comment box. It opens five lines tall and grows with the text
 * until it fills a share of the conversation, after which it scrolls. An agent can
 * drag its top edge to set a taller floor, or expand it outright; whichever they chose
 * last is remembered in this browser, so the box opens the way they like it.
 *
 * Heights are border-box pixels of the editable area, padding included, because that
 * is the box `minHeight` / `maxHeight` act on.
 */

export const START_LINES = 5;

/** Where growing stops, as a share of the conversation (activity plus composer). */
export const GROW_LIMIT_SHARE = 0.45;

/** How far a drag may go, so the activity is never squeezed out entirely. */
export const DRAG_LIMIT_SHARE = 0.8;

export type CommentBoxHeights = { minHeight: number; maxHeight: number };

/** The least of the activity that stays in view, however large the box is made. */
export const MIN_ACTIVITY_PX = 48;

export const startHeight = (lineHeight: number, padding: number): number =>
  START_LINES * lineHeight + padding;

/**
 * The tallest the text area may be while leaving MIN_ACTIVITY_PX of the activity. The
 * shares below are of the conversation and apply to the text area alone, so on a phone,
 * where the toolbar and the wrapped status row around it take ~190px, they would
 * otherwise push the box up past the activity and over the tabs above it. `chromeHeight`
 * is the box's height less its text area.
 */
export const activityCeiling = (conversationHeight: number, chromeHeight: number): number =>
  conversationHeight - chromeHeight - MIN_ACTIVITY_PX;

/**
 * A dragged height is a floor, not a fixed size: the box still grows past it up to
 * the limit, and the limit rises to meet it when the agent has dragged beyond it.
 * Nothing passes `ceiling` except the starting height, which a very short screen keeps.
 */
export const getCommentBoxHeights = (
  conversationHeight: number,
  lineHeight: number,
  padding: number,
  draggedHeight: number | null,
  ceiling = Number.POSITIVE_INFINITY
): CommentBoxHeights => {
  const start = startHeight(lineHeight, padding);
  const minHeight = Math.max(start, Math.min(draggedHeight ?? 0, ceiling));
  const limit = Math.min(Math.round(conversationHeight * GROW_LIMIT_SHARE), ceiling);

  return { minHeight, maxHeight: Math.max(limit, minHeight) };
};

export const clampDraggedHeight = (
  height: number,
  conversationHeight: number,
  lineHeight: number,
  padding: number,
  ceiling = Number.POSITIVE_INFINITY
): number => {
  const lowest = startHeight(lineHeight, padding);
  const highest = Math.max(lowest, Math.min(Math.round(conversationHeight * DRAG_LIMIT_SHARE), ceiling));

  return Math.round(Math.min(highest, Math.max(lowest, height)));
};

/** Expanded, the box takes as much as a drag may, leaving the activity a strip. */
export const expandedHeights = (
  conversationHeight: number,
  lineHeight: number,
  padding: number,
  ceiling = Number.POSITIVE_INFINITY
): CommentBoxHeights => {
  const height = clampDraggedHeight(Number.MAX_SAFE_INTEGER, conversationHeight, lineHeight, padding, ceiling);
  return { minHeight: height, maxHeight: height };
};

/**
 * The cursor over the box's top edge: thin up and down chevrons. Built here rather than in
 * the stylesheet so it takes its colour from the theme - an image cannot read a CSS
 * variable. ns-resize where a browser does not draw custom cursors.
 */
export const resizeCursor = (color: string): string => {
  const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" '
    + 'stroke-linecap="round" stroke-linejoin="round">'
    + `<path d="M7 9.5l5-5 5 5M7 14.5l5 5 5-5" stroke="${color}" stroke-width="1.25"/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 12 12, ns-resize`;
};

const storageKey = (user: string) => `tikit.commentBoxHeight.${user}`;

export const readDraggedHeight = (user: string): number | null => {
  try {
    const value = Number(localStorage.getItem(storageKey(user)));
    return value > 0 ? value : null;
  } catch {
    return null;
  }
};

export const saveDraggedHeight = (user: string, height: number | null): void => {
  try {
    if (height === null) localStorage.removeItem(storageKey(user));
    else localStorage.setItem(storageKey(user), String(height));
  } catch {
    // Storage blocked or full: the height simply is not remembered.
  }
};

const expandedKey = (user: string) => `tikit.commentBoxExpanded.${user}`;

export const readExpanded = (user: string): boolean => {
  try {
    return localStorage.getItem(expandedKey(user)) === 'true';
  } catch {
    return false;
  }
};

export const saveExpanded = (user: string, expanded: boolean): void => {
  try {
    if (expanded) localStorage.setItem(expandedKey(user), 'true');
    else localStorage.removeItem(expandedKey(user));
  } catch {
    // Storage blocked or full: the choice simply is not remembered.
  }
};
